import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  stainRequestService,
  RequestAdditionalStainDto,
  RequestAdditionalStainsBatchDto,
  PendingStainInfo,
  StainSearchResult,
  StainProvider,
  ToggleSlideDoneResult,
  SpecialStainInfo,
  type AdditionalStainRequestSurface,
} from "../services/stainRequest.service";

// ==================== QUERY KEYS ====================

export const stainRequestKeys = {
  all: ["stain-requests"] as const,
  pending: (caseId: string) =>
    [...stainRequestKeys.all, "pending", caseId] as const,
  search: (query: string) =>
    [...stainRequestKeys.all, "search", query] as const,
  providers: (stainId: string) =>
    [...stainRequestKeys.all, "providers", stainId] as const,
};

// ==================== QUERIES ====================

/**
 * Hook to get pending stain requests for a case
 */
export function usePendingStains(caseId: string | undefined) {
  return useQuery<PendingStainInfo[]>({
    queryKey: stainRequestKeys.pending(caseId!),
    queryFn: () => stainRequestService.getPendingStains(caseId!),
    enabled: !!caseId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Hook to search stains for the request modal
 */
export function useStainSearch(query: string, enabled = true) {
  return useQuery<StainSearchResult[]>({
    queryKey: stainRequestKeys.search(query),
    queryFn: () => stainRequestService.searchStainsForRequest(query, 10),
    enabled: enabled && query.length >= 1,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Hook to get providers for a specific stain
 */
export function useStainProviders(stainId: string | undefined) {
  return useQuery<StainProvider[]>({
    queryKey: stainRequestKeys.providers(stainId!),
    queryFn: () => stainRequestService.getProvidersForStain(stainId!),
    enabled: !!stainId,
    staleTime: 300000, // Cache for 5 minutes (providers don't change often)
  });
}

// ==================== MUTATIONS ====================

/**
 * Hook to request an additional stain
 */
export function useRequestAdditionalStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: RequestAdditionalStainDto;
    }) => stainRequestService.requestAdditionalStain(caseId, data),
    onSuccess: (_, variables) => {
      // Invalidate pending stains for this case
      queryClient.invalidateQueries({
        queryKey: stainRequestKeys.pending(variables.caseId),
      });
      // Also invalidate the case data to reflect status change
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", variables.caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["pathologist-review-queue"],
      });
    },
  });
}

/**
 * Hook to request a batch of additional stains (one child case, multiple slides)
 */
export function useRequestAdditionalStainsBatch(
  surface: AdditionalStainRequestSurface = "pathologist-review"
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: RequestAdditionalStainsBatchDto;
    }) =>
      stainRequestService.requestAdditionalStainsBatch(caseId, data, surface),
    onSuccess: (_, variables) => {
      // Invalidate pending stains for this case
      queryClient.invalidateQueries({
        queryKey: stainRequestKeys.pending(variables.caseId),
      });
      // Also invalidate the case data to reflect status change
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", variables.caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case", variables.caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-additional-stains", variables.caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["pathologist-review-queue"],
      });
    },
  });
}

/**
 * Queries that show slides or slide-derived counts anywhere in the app. A stain
 * can be deleted from cutting, staining, case detail or pathologist review, so
 * every one of these has to refresh regardless of where the delete came from.
 */
const SLIDE_DEPENDENT_QUERY_KEYS = [
  ["staining-slides"],
  ["staining-queue"],
  ["staining-stats"],
  ["vendor-progress"],
  ["cutting-queue"],
  ["cutting-queue-page"],
  ["cutting-queue-context"],
  ["pending-distribution"],
  ["distribution-queue"],
  ["distribution-counts"],
  ["distribution-case-slides"],
  ["cases"],
  ["case-stats"],
  ["pathologist-review-queue"],
];

/** Same, but scoped to the case the slide belonged to. */
const caseScopedQueryKeys = (caseId: string) => [
  ["case-for-cutting", caseId],
  ["case-specimens", caseId],
  ["case-slides", caseId],
  ["case-blocks", caseId],
  ["case", caseId],
  ["case-status-history", caseId],
  ["case-for-pathologist-review", caseId],
  ["pathologist-review", caseId, "panels"],
  ["case-additional-stains", caseId],
  stainRequestKeys.pending(caseId),
];

/**
 * Hook to delete a stain slide from a case.
 * `caseId` is required — without it the case-scoped views silently go stale.
 */
export function useDeleteStain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slideId,
      reason,
    }: {
      slideId: string;
      caseId: string;
      reason?: string | null;
    }) => stainRequestService.deleteStain(slideId, reason),
    onSuccess: (_, variables) => {
      [
        ...SLIDE_DEPENDENT_QUERY_KEYS,
        ...caseScopedQueryKeys(variables.caseId),
      ].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
  });
}

/**
 * Hook to toggle the Done (isCompleted) flag on a slide
 */
export function useToggleSlideDone(caseId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<ToggleSlideDoneResult, Error, string>({
    mutationFn: (slideId: string) =>
      stainRequestService.toggleSlideDone(slideId),
    onSuccess: () => {
      // Invalidate case data so the slide's isCompleted state refreshes
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      // Also refresh pending stains so the warning modal reflects the toggle
      queryClient.invalidateQueries({
        queryKey: stainRequestKeys.pending(caseId!),
      });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
      SLIDE_DEPENDENT_QUERY_KEYS.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      if (caseId) {
        caseScopedQueryKeys(caseId).forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
      // A return can choose a different child, so refresh all open child views.
      queryClient.invalidateQueries({ queryKey: ["case-for-cutting"] });
      queryClient.invalidateQueries({ queryKey: ["case-additional-stains"] });
    },
  });
}

// ==================== HELPER HOOKS ====================

/**
 * Hook to check if case has pending stains
 */
export function useHasPendingStains(caseId: string | undefined) {
  const { data: pendingStains } = usePendingStains(caseId);
  return {
    hasPending: (pendingStains?.length ?? 0) > 0,
    count: pendingStains?.length ?? 0,
    pendingStains,
  };
}

/**
 * Hook to get all active stains available for stain/CPT correction
 */
export function useSpecialStains() {
  return useQuery<SpecialStainInfo[]>({
    queryKey: ["special-stains"],
    queryFn: () => stainRequestService.getSpecialStains(),
    staleTime: 600000, // Cache for 10 minutes (stains rarely change)
  });
}

/**
 * Hook to correct a slide's CPT code
 */
export function useCorrectSlideCpt(caseId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slideId,
      newStainId,
      reason,
    }: {
      slideId: string;
      newStainId: string;
      reason: string;
    }) => stainRequestService.correctSlideCpt(slideId, { newStainId, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
    },
  });
}
